import React, { useState } from 'react';
import PropTypes from 'prop-types';
import { format, parseISO } from 'date-fns';
import { TableCell, TableRow } from '@material-ui/core';
import { policyTypeNames } from '@ourbranch/policy-types';

import icInfoDark from 'core/assets/svg/ic-info-dark-16.svg';
import icRightArrow from 'core/assets/svg/ic-right-arrow.svg';
import BaseDialog from 'core/components/base-dialog';
import { Button } from 'core/components/button';
import { Label } from 'core/components/label';
import LabelWithCopyButton from 'core/components/label-with-copy-button';
import { StatusPill } from 'core/components/status-pill';
import { Table } from 'core/components/table';
import { SHORT_LAYMAN_DATE_FORMAT, currencyFormatter, shortLaymanDateTimeFormatter } from 'core/helpers/formatters';
import useStyles from './policy-drafts-modal.styles';

const DRAFTS_PER_PAGE = 10;

const PolicyDraftsModal = ({
  onClose,
  onGoToOfficialPolicy,
  onOpenDraft,
  policyId,
  policyholderName,
  policyType,
  previewDrafts
}) => {
  const classes = useStyles();
  const [page, setPage] = useState(0);
  const pageDrafts = previewDrafts.slice(page * DRAFTS_PER_PAGE, (page + 1) * DRAFTS_PER_PAGE);

  return (
    <BaseDialog size="lg" open onClose={onClose} className={classes.dialog}>
      <div className={classes.header}>
        <Label type="h1">
          Endorsement drafts for {policyholderName}/{policyTypeNames[policyType]} policy
        </Label>
        <div className={classes.policyId}>
          <Label type="body4">POLICY ID</Label>
          <LabelWithCopyButton type="body3">{policyId}</LabelWithCopyButton>
        </div>
      </div>
      <Label type="body1" className={classes.subtitle}>
        Endorsement drafts are copies of the policyholder&apos;s current policy with proposed changes applied.
      </Label>
      <Table
        className={classes.table}
        paginationEnabled
        count={previewDrafts.length}
        rowsPerPage={DRAFTS_PER_PAGE}
        currentPage={page}
        onPageChange={(_, nextPage) => setPage(nextPage)}
        header={
          <>
            <TableCell className={classes.headerCell}>Endorsement date</TableCell>
            <TableCell className={classes.headerCell}>Last Updated</TableCell>
            <TableCell className={classes.headerCell}>Created by</TableCell>
            <TableCell className={classes.headerCell}>Premium</TableCell>
            <TableCell className={classes.headerCell}>Status</TableCell>
            <TableCell />
          </>
        }
        body={pageDrafts.map((draft) => (
          <TableRow key={draft.previewId} className={classes.row} onClick={() => onOpenDraft(draft.previewId)}>
            <TableCell className={classes.bodyCell}>
              {draft.effectiveDate ? format(parseISO(draft.effectiveDate), SHORT_LAYMAN_DATE_FORMAT) : '—'}
            </TableCell>
            <TableCell className={classes.bodyCell}>{shortLaymanDateTimeFormatter(draft.lastUpdatedAt)}</TableCell>
            <TableCell className={classes.bodyCell}>{draft.ownedBy}</TableCell>
            <TableCell className={classes.bodyCell}>
              {draft.premium === null ? '—' : currencyFormatter(draft.premium)}
            </TableCell>
            <TableCell>
              <StatusPill status={draft.status} />
            </TableCell>
            <TableCell align="right">
              <span className={classes.openDraft}>
                Open draft <img src={icRightArrow} alt="" />
              </span>
            </TableCell>
          </TableRow>
        ))}
      />
      <div className={classes.note}>
        <img src={icInfoDark} alt="" />
        <Label type="body2" className={classes.noteText}>
          These drafts will remain here until an endorsement is issued for this policy. Once an endorsement is issued,
          all other drafts will be removed.
        </Label>
      </div>
      <div className={classes.actions}>
        <Button variant="contained" color="secondary" onClick={onGoToOfficialPolicy} className={classes.actionButton}>
          Go to official policy
        </Button>
        <Button variant="contained" color="primary" onClick={onClose} className={classes.actionButton}>
          Close
        </Button>
      </div>
    </BaseDialog>
  );
};

PolicyDraftsModal.propTypes = {
  onClose: PropTypes.func.isRequired,
  onGoToOfficialPolicy: PropTypes.func.isRequired,
  onOpenDraft: PropTypes.func.isRequired,
  policyId: PropTypes.string.isRequired,
  policyholderName: PropTypes.string.isRequired,
  policyType: PropTypes.string.isRequired,
  previewDrafts: PropTypes.array.isRequired
};

export default PolicyDraftsModal;
